import { Location } from "@models/Location";
import { ComputedRef, computed, reactive } from "vue";
import useValidators from "@/modules/validators";
import { ValidationContext } from "@/models/Context";

export default function useFormValidation(context?: ValidationContext | undefined | null): {
  errors: Map<string, string>;
  clearErrors: () => void;
  clearError: (fieldName: string) => void;
  validators: Record<string, any>;
  validateRequiredField: (fieldName: string, fieldLabel: string, fieldValue: string) => void;
  validateRequiredDateField: (fieldName: string, fieldLabel: string, fieldValue: Date|undefined) => void;
  validateRequiredNumberField: (fieldName: string, fieldLabel: string, fieldValue: number) => void;
  validateEmailField: (fieldName: string, fieldLabel: string, fieldValue: string) => void;
  validatePhoneField: (fieldName: string, fieldLabel: string, fieldValue: string) => void;
  validatePasswordField: (fieldName: string, fieldLabel: string, fieldValue: string) => void;
  validateRegexField: (
    fieldName: string,
    fieldValue: string,
    pattern: string,
    patternMessage: string
  ) => void;
  validateLocation: (fieldName: string, fieldLabel: string, location: Location) => void;
  hasErrors: ComputedRef<boolean>;
  hasError:(fieldName: string) => ComputedRef<boolean>;
  validateAll: () => void;
  clearValidator: (key: string) => void;
  registerValidator: (key: string, validator: (key:string, validator:string) => void) => void;
  // eslint-disable-next-line indent
} {

  // Ensure errors is always a Map with get/set methods
  let errors: Map<string, string>;

  if (context?.errors && typeof context.errors.get === 'function') {
    // If context.errors exists and has a get method (either a Map or reactive Map), use it
    errors = context.errors;
  } else {
    // Otherwise, create a new reactive Map
    errors = reactive<Map<string, string>>(new Map<string, string>());
  }

  const validators = context?.validators ?? {};
  const { isEmpty, isEmptyDate, isEmptyNumber, minLength, isEmail, isNum, regExTest } =
    useValidators();

  const validateRequiredField = (fieldName: string, fieldLabel: string, fieldValue: string) => {
    errors.set(fieldName, isEmpty(fieldLabel, fieldValue));
  };

  const validateRequiredNumberField = (
    fieldName: string,
    fieldLabel: string,
    fieldValue: number
  ) => {
    errors.set(fieldName, isEmptyNumber(fieldLabel, fieldValue));
  };

  const validateRequiredDateField = (fieldName: string, fieldLabel: string, fieldValue: Date|undefined) => {
    errors.set(fieldName, isEmptyDate(fieldLabel, fieldValue));
  };

  const validateLocation = (fieldName: string, fieldLabel: string, location: Location | null) => {
    // eslint-disable-next-line no-underscore-dangle
    if (location && location._lat && location._long) {
      errors.set(fieldName, "");
      return;
    }
    errors.set(fieldName, `${fieldLabel} is required.`);
  };

  const validateEmailField = (fieldName: string, fieldLabel: string, fieldValue: string) => {
    const result = !fieldValue ? isEmpty(fieldLabel, fieldValue) : isEmail(fieldLabel, fieldValue);
    errors.set(fieldName, result);
  };

  const validatePhoneField = (fieldName: string, fieldLabel: string, fieldValue: string) => {
    const result = !fieldValue ? isEmpty(fieldLabel, fieldValue) : isNum(fieldLabel, fieldValue);
    errors.set(fieldName, result);
  };

  const validatePasswordField = (fieldName: string, fieldLabel: string, fieldValue: string) => {
    const result = !fieldValue
      ? isEmpty(fieldLabel, fieldValue)
      : minLength(fieldLabel, fieldValue, 8);
    errors.set(fieldName, result);
  };

  const validateRegexField = (
    fieldName: string,
    fieldValue: string,
    pattern: string,
    patternMessage: string
  ) => {
    const result = regExTest(fieldValue, pattern, patternMessage);
    errors.set(fieldName, result);
  };

  const hasError = (fieldName:string) => computed(() => (errors.get(fieldName) ?? "") !== "");

  const hasErrors = computed(
    () => Array.from(errors.values()).filter((x) => x !== "").length > 0
  )

  const validateAll = () => {
    Object.values(validators).forEach((validator) => {
      if (validator) {
        validator();
      }
    });
  };

  const clearError = (fieldName:string) => {
    errors.set(fieldName,"");
  };

  const clearErrors = () => {
    // Clear all errors using Map's clear method
    errors.clear();
  };

  const clearValidator = (key: string) => {
    clearError(key);
    delete validators[key];
  }

  const registerValidator = (key: string, validator: (key: string, validator: string) => void) => {
    validators[key] = validator;
  };

  return {
    errors,
    clearErrors,
    clearError,
    validators,
    validateRequiredField,
    validateRequiredDateField,
    validateRequiredNumberField,
    validateEmailField,
    validatePhoneField,
    validatePasswordField,
    validateRegexField,
    validateLocation,
    hasErrors,
    hasError,
    validateAll,
    clearValidator,
    registerValidator
  };
}
